import '../../i18n/autoTranslate';
import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ReferenceLine,
} from 'recharts';
import SpotlightCard from '../../../components/ui/SpotlightCard';
import Icon from '../../../components/AppIcon';

const safeNum = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);

const PerformanceChart = ({ data = [], timeRange = 'month' }) => {
  const [metric, setMetric] = useState('score');

  const hasSkillSignals = useMemo(() => {
    return (data || []).some(
      (d) =>
        safeNum(d?.technical, 0) ||
        safeNum(d?.communication, 0) ||
        safeNum(d?.problem_solving, 0) ||
        safeNum(d?.behavioral, 0)
    );
  }, [data]);

  const ranges = {
    week: 'Last 7 days',
    month: 'Last 4 weeks',
    quarter: 'Last 3 months',
    year: 'Last 12 months',
  };

  const summary = useMemo(() => {
    const points = (data || []).filter((d) => safeNum(d?.sessions, 0) > 0);
    const avg = points.length
      ? points.reduce((s, p) => s + safeNum(p.score, 0), 0) / points.length
      : 0;
    const best = points.length
      ? Math.max(...points.map((p) => safeNum(p.score, 0)))
      : 0;
    const sessions = (data || []).reduce(
      (s, p) => s + safeNum(p.sessions, 0),
      0
    );
    return {
      avg: Math.round(avg * 10) / 10,
      best: Math.round(best * 10) / 10,
      sessions,
    };
  }, [data]);

  const tabs = [
    { key: 'score', label: 'Overall', icon: 'TrendingUp' },
    ...(hasSkillSignals
      ? [
          { key: 'technical', label: 'Technical', icon: 'Code' },
          { key: 'communication', label: 'Communication', icon: 'MessageSquareText' },
          { key: 'problem_solving', label: 'Problem Solving', icon: 'Brain' },
          { key: 'behavioral', label: 'Behavioral', icon: 'Users' },
        ]
      : []),
  ];

  const activeTab = tabs.find((t) => t.key === metric) || tabs[0];

  // FIX: tooltip now color-codes each row using the actual series color so
  // multi-line readouts are scannable.
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const get = (key) => payload.find((p) => p.dataKey === key);
    const score = get('score');
    const sessions = get('sessions');
    const skill = metric !== 'score' ? get(metric) : null;

    return (
      <div className="rounded-2xl border border-white/10 bg-[#080a0f]/92 px-4 py-3 shadow-2xl backdrop-blur-xl">
        <div className="text-sm font-semibold text-white mb-2">{label}</div>
        <div className="space-y-1.5 text-sm">
          {score ? (
            <div className="flex items-center justify-between gap-6">
              <span className="inline-flex items-center gap-2 text-white/70">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: score.color || score.stroke }}
                />
                Score
              </span>
              <span className="text-white font-semibold tabular-nums">
                {Math.round(safeNum(score.value, 0))}
              </span>
            </div>
          ) : null}
          {skill ? (
            <div className="flex items-center justify-between gap-6">
              <span className="inline-flex items-center gap-2 text-white/70">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: skill.color || skill.stroke }}
                />
                {activeTab.label}
              </span>
              <span className="text-white font-semibold tabular-nums">
                {Math.round(safeNum(skill.value, 0))}
              </span>
            </div>
          ) : null}
          {sessions ? (
            <div className="flex items-center justify-between gap-6 pt-1 border-t border-white/8">
              <span className="inline-flex items-center gap-2 text-white/70">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.45)' }}
                />
                Sessions
              </span>
              <span className="text-white/85 font-semibold tabular-nums">
                {safeNum(sessions.value, 0)}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  // Empty state
  const isEmpty = !data?.length || summary.sessions === 0;

  // Sessions Y-axis upper bound, padded a little so bars don't kiss the top
  const sessionsMax = useMemo(() => {
    const m = Math.max(0, ...((data || []).map((d) => safeNum(d?.sessions, 0))));
    return Math.max(4, Math.ceil(m * 1.25));
  }, [data]);

  return (
    <SpotlightCard
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]"
      spotlightColor="rgba(99,102,241,0.14)"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-28 -right-24 h-[340px] w-[340px] rounded-full bg-violet-500/14 blur-[90px]" />
        <div className="absolute -bottom-28 -left-24 h-[340px] w-[340px] rounded-full bg-sky-500/10 blur-[90px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/45" />
      </div>

      <div className="relative z-10 p-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white">
              Performance trend
            </div>
            <div className="text-xs text-white/55 mt-1">
              {ranges[timeRange] || 'Trend'} • {summary.sessions} sessions
              included
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {tabs.map((t) => {
                const active = metric === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setMetric(t.key)}
                    aria-pressed={active}
                    className={[
                      'inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-xs font-semibold transition',
                      active
                        ? 'border-white/14 bg-white/[0.10] text-white'
                        : 'border-white/10 bg-white/[0.03] text-white/70 hover:text-white hover:bg-white/[0.06]',
                    ].join(' ')}
                  >
                    <Icon
                      name={t.icon}
                      size={14}
                      className={active ? 'text-white' : 'text-white/70'}
                    />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl px-4 py-2">
              <div className="text-[11px] text-white/55">Average</div>
              <div className="text-sm font-semibold text-white tabular-nums">
                {summary.avg || 0}
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl px-4 py-2">
              <div className="text-[11px] text-white/55">Best</div>
              <div className="text-sm font-semibold text-white tabular-nums">
                {summary.best || 0}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 h-[340px]">
          {isEmpty ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-2 text-white/55">
              <Icon name="LineChart" size={28} className="text-white/40" />
              <div className="text-sm">
                No data yet for this range — complete a few sessions and your
                trend will appear here.
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              {/*
                FIX 1 — bars used to share the [0,100] axis with score, so
                sessions (typically 1–8) collapsed to a 1px sliver at the
                bottom. We now use TWO Y-axes: left for score (0–100), right
                for sessions (auto-scaled).

                FIX 2 — the original placed <Bar> BEFORE <Area>, so the area
                gradient drew over the bars. We now render: bars first
                (background), reference line for average, area on top (the
                hero metric), and an optional skill line overlay.

                FIX 3 — added gradient defs so the area has the polished
                fade-out it deserves, and a subtle activeDot so users get
                clear hover feedback.
              */}
              <ComposedChart
                data={data || []}
                margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="scoreFillGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(165,180,252,0.55)" />
                    <stop offset="100%" stopColor="rgba(99,102,241,0.04)" />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.06)"
                  vertical={false}
                />
                <XAxis
                  dataKey="period"
                  tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 12 }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.10)' }}
                  tickLine={false}
                  minTickGap={16}
                />
                <YAxis
                  yAxisId="score"
                  domain={[0, 100]}
                  tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 12 }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.10)' }}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="sessions"
                  orientation="right"
                  domain={[0, sessionsMax]}
                  allowDecimals={false}
                  tick={{ fill: 'rgba(255,255,255,0.42)', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={30}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ stroke: 'rgba(255,255,255,0.12)', strokeWidth: 1 }}
                />
                <Legend
                  wrapperStyle={{
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: 12,
                    paddingTop: 6,
                  }}
                  iconType="circle"
                />

                {/* Sessions volume — drawn first so the score area sits on top */}
                <Bar
                  yAxisId="sessions"
                  dataKey="sessions"
                  name="Sessions"
                  fill="rgba(255,255,255,0.16)"
                  radius={[8, 8, 0, 0]}
                  maxBarSize={28}
                  isAnimationActive={false}
                />

                {/* Average reference line */}
                {summary.avg > 0 ? (
                  <ReferenceLine
                    yAxisId="score"
                    y={summary.avg}
                    stroke="rgba(255,255,255,0.18)"
                    strokeDasharray="4 4"
                    label={{
                      value: `Avg ${summary.avg}`,
                      fill: 'rgba(255,255,255,0.55)',
                      fontSize: 11,
                      position: 'right',
                    }}
                  />
                ) : null}

                {/* Overall score — hero metric */}
                <Area
                  yAxisId="score"
                  type="monotone"
                  dataKey="score"
                  name="Overall Score"
                  stroke="rgba(165,180,252,0.95)"
                  fill="url(#scoreFillGrad)"
                  strokeWidth={2.4}
                  dot={false}
                  activeDot={{
                    r: 5,
                    stroke: 'rgba(165,180,252,1)',
                    strokeWidth: 2,
                    fill: '#0c1220',
                  }}
                  isAnimationActive={false}
                />

                {/* Selected skill overlay */}
                {metric !== 'score' ? (
                  <Line
                    yAxisId="score"
                    type="monotone"
                    dataKey={metric}
                    name={activeTab.label}
                    stroke="rgba(52,211,153,0.95)"
                    strokeWidth={2.4}
                    dot={{
                      r: 3,
                      fill: '#0c1220',
                      stroke: 'rgba(52,211,153,1)',
                      strokeWidth: 2,
                    }}
                    activeDot={{
                      r: 5,
                      stroke: 'rgba(52,211,153,1)',
                      strokeWidth: 2,
                      fill: '#0c1220',
                    }}
                    isAnimationActive={false}
                  />
                ) : null}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        {!hasSkillSignals && !isEmpty ? (
          <div className="mt-4 text-xs text-white/55">
            Skill breakdown will appear once your sessions include category
            scores (technical, communication, problem-solving, behavioral).
          </div>
        ) : null}
      </div>
    </SpotlightCard>
  );
};

export default PerformanceChart;
